import {ButtonRadioGroup} from "@/shared/ui/buttonRadioGroup";
import {CompensationType} from "@/shared/timrTypes";
import {floatToPercent} from "@/shared/utils/misc";
import {formatDuration, parseDuration} from "@/shared/utils/datefunctions";

export enum CompensationCreditabilityMode {
	FullCreditability = "FULL_CREDITABILITY",
	Compensation = "COMPENSATION",
	Creditability = "CREDITABILITY"
}

export type CompensationCreditabilityType =
	"creditability"
	| "compensationCreditability"
	| "compensationDeductible"
	| "nonCreditableDeductible";

export interface CreditabilityCompensationValues {
	creditability: number;
	compensationCreditability: number;
	nonCreditableDeductible: number;
	compensationDeductible: number;
	compensationType: CompensationType | null;
}

// Uniform needs a unique name for the radio buttons, so we generate a UUID for that
// otherwise, multiple instances of this component would interfere with each other
// This took way to long to figure out...
const getTemplate = (uuid: string): string => `
		<div class="btn-group">
			<button type="button" class="btn btn-default" data-button-full-creditability>
				${messages["workingTimeType.compensationCreditability.fullCreditability"]}
			</button>
			<button type="button" class="btn btn-default" data-button-compensation>
				${messages["workingTimeType.compensationCreditability.compensation"]}
			</button>
			<button type="button" class="btn btn-primary" data-button-creditability>
				${messages["workingTimeType.compensationCreditability.creditability"]}
			</button>
		</div>
		<div class="flex-row" data-compensation-container style="display:none;">
			<div class="flex-column-auto">
				<div class="radio form-group" style="margin-top: 8px;">
					<label class="radio-inline display-flex">
						<input type="radio" name="compensationConfig-${uuid}" value="compensationCreditability" data-radio="compensationCreditability" checked/>
						<div class="input-group" style="margin-top:-8px;">
							<input type="number" data-creditability-value="compensationCreditability" data-compensation-creditability-percent-input min="0" class="form-control" style="width: 80px;">
							<span class="input-group-addon">%</span>
						</div>
					</label>
				</div>
				<div class="radio form-group" data-deductible>
					<label class="radio-inline display-flex text-nowrap">
						${messages["workingTimeType.compensationCreditability.compensationDeductible"]}
						<input type="radio" name="compensationConfig-${uuid}" value="compensationDeductible" data-radio="compensationDeductible">
						<div class="input-group" style="margin-top:-8px; margin-left:10px;">
							<input type="text" data-creditability-value="compensationDeductible" name="nonCompensationDeductible-${uuid}" data-compensation-deductible-input class="form-control" style="width: 70px;"/>
							<span class="input-group-addon">${messages["workingTimeType.compensationCreditability.hours"]}</span>
						</div>
					</label>
				</div>
			</div>
			<div class="flex-column-40" data-info>
				<div class="input-info-text no-padding-top" style="margin-top:8px;">
					<span data-creditability-info="compensationCreditability" style="display:none;">
						<i class="far fa-info-circle position-left"></i>
						${messages["workingTimeType.compensationCreditability.info.compensationCreditability"]}
					</span>
					<span data-creditability-info="compensationDeductible" style="display:none;">
						<i class="far fa-info-circle position-left"></i>
						${messages["workingTimeType.compensationCreditability.info.compensationDeductible"]}
					</span>
				</div>
			</div>
		</div>
		<div class="flex-row" data-creditability-container>
			<div class="flex-column-auto">
				<div class="radio form-group" style="margin-top: 8px;">
					<label class="radio-inline display-flex">
						<input type="radio" name="creditabilityConfig-${uuid}" value="creditability" data-radio="creditability" checked/>
						<div class="input-group" style="margin-top:-8px;">
							<input type="number" data-creditability-value="creditability" data-creditability-percent-input min="0" class="form-control" style="width: 80px;">
							<span class="input-group-addon">%</span>
						</div>
					</label>
				</div>
				<div class="radio form-group" data-deductible>
					<label class="radio-inline display-flex text-nowrap">
						${messages["workingTimeType.compensationCreditability.nonCreditableDeductible"]}
						<input type="radio" name="creditabilityConfig-${uuid}" value="nonCreditableDeductible" data-radio="nonCreditableDeductible">
						<div class="input-group" style="margin-top:-8px; margin-left:10px;">
							<input type="text" data-creditability-value="nonCreditableDeductible" name="nonCreditableDeductible-${uuid}" data-non-creditable-deductible-input class="form-control" style="width: 70px;"/>
							<span class="input-group-addon">${messages["workingTimeType.compensationCreditability.hours"]}</span>
						</div>
					</label>
				</div>
			</div>
			<div class="flex-column-40" data-info>
				<div class="input-info-text no-padding-top" style="margin-top:8px;">
					<span data-creditability-info="creditability" style="display:none;">
						<i class="far fa-info-circle position-left"></i>
						${messages["workingTimeType.compensationCreditability.info.creditability"]}
					</span>
					<span data-creditability-info="nonCreditableDeductible" style="display:none;">
						<i class="far fa-info-circle position-left"></i>
						${messages["workingTimeType.compensationCreditability.info.nonCreditableDeductible"]}
					</span>
				</div>
			</div>
		</div>
	`;

export class CreditabilityCompensationComponent extends HTMLElement {
	private static readonly PERCENTAGE_ONLY_ATTRIBUTE: string = "percentage-only";
	private static readonly COMPACT_ATTRIBUTE: string = "compact";

	public static readonly EVENT_CHANGED: string = "creditabilityCompensationComponent.changed";
	private compensationCreditabilityMode: ButtonRadioGroup<CompensationCreditabilityMode> | null = null;

	private uuid = crypto.randomUUID();

	private _values: CreditabilityCompensationValues = {
		creditability: 1,
		compensationCreditability: 1,
		nonCreditableDeductible: 0,
		compensationDeductible: 0,
		compensationType: CompensationType.FULL
	};

	public get values(): CreditabilityCompensationValues {
		return {
			creditability: this._values.creditability,
			compensationCreditability: this._values.compensationCreditability,
			nonCreditableDeductible: this._values.nonCreditableDeductible,
			compensationDeductible: this._values.compensationDeductible,
			compensationType: this._values.compensationType
		};
	}

	public set values(v: CreditabilityCompensationValues) {
		this._values = {...v};
		this.updateUI();
		this.compensationCreditabilityMode?.setValue(this.determineMode());
		this.selectRadio(this.determineRadioValue());
	}

	public getMode(): CompensationCreditabilityMode {
		return this.compensationCreditabilityMode?.getValue() ?? CompensationCreditabilityMode.FullCreditability;
	}

	getSelectedCompensationType(): CompensationCreditabilityType {
		return this.querySelector<HTMLInputElement>(`input[type='radio'][name='compensationConfig-${this.uuid}']:checked`)?.value as CompensationCreditabilityType;
	}

	getSelectedCreditabilityType(): CompensationCreditabilityType {
		return this.querySelector<HTMLInputElement>(`input[type='radio'][name='creditabilityConfig-${this.uuid}']:checked`)?.value as CompensationCreditabilityType;
	}

	protected connectedCallback(): void {
		this.innerHTML = getTemplate(this.uuid);
		if (this.hasAttribute(CreditabilityCompensationComponent.PERCENTAGE_ONLY_ATTRIBUTE)) {
			this.querySelectorAll("[data-deductible]").forEach(el => el.remove());
		}
		if (this.hasAttribute(CreditabilityCompensationComponent.COMPACT_ATTRIBUTE)) {
			this.querySelectorAll("[data-info]").forEach(el => el.remove());
		}
		this.initButtonRadioGroup();
		this.initFieldObservers();
		this.updateUI();
		this.compensationCreditabilityMode?.setValue(this.determineMode());
		this.selectRadio(this.determineRadioValue());
	}

	private initButtonRadioGroup(): void {
		const buttonFullCreditability = this.querySelector("[data-button-full-creditability]");
		const buttonCompensation = this.querySelector("[data-button-compensation]");
		const buttonCreditability = this.querySelector("[data-button-creditability]");

		if (!buttonFullCreditability || !buttonCompensation || !buttonCreditability) {
			return;
		}

		this.compensationCreditabilityMode = ButtonRadioGroup.typed({
			value: CompensationCreditabilityMode.FullCreditability,
			button: buttonFullCreditability as HTMLElement
		}, {
			value: CompensationCreditabilityMode.Compensation,
			button: buttonCompensation as HTMLElement
		}, {
			value: CompensationCreditabilityMode.Creditability,
			button: buttonCreditability as HTMLElement
		}).onValueChanged((mode: CompensationCreditabilityMode) => {
			const creditabilityContainer: HTMLElement = this.querySelector("[data-creditability-container]");
			const compensationContainer: HTMLElement = this.querySelector("[data-compensation-container]");
			creditabilityContainer.style.display = mode === CompensationCreditabilityMode.Creditability ? "" : "none";
			compensationContainer.style.display = mode === CompensationCreditabilityMode.Compensation ? "" : "none";
			this.selectRadio(this.determineRadioValue());
			this.fireChangedEventAndUpdateUI();
		});
	}

	private initFieldObservers(): void {
		this.querySelector("[data-compensation-deductible-input]")
			?.addEventListener("change", (e) => this.onFieldValueChange("compensationDeductible",
				parseDuration((e.target as HTMLInputElement).value, true) * 60000)
			);
		this.querySelector("[data-non-creditable-deductible-input]")
			?.addEventListener("change", (e) => this.onFieldValueChange("nonCreditableDeductible",
				parseDuration((e.target as HTMLInputElement).value, true) * 60000)
			);
		this.querySelector("[data-compensation-creditability-percent-input]")
			?.addEventListener("change", (e) => this.onFieldValueChange("compensationCreditability",
				Number((e.target as HTMLInputElement).value) / 100)
			);
		this.querySelector("[data-creditability-percent-input]")
			?.addEventListener("change", (e) => this.onFieldValueChange("creditability",
				Number((e.target as HTMLInputElement).value) / 100)
			);

		// listeners for creditability and compensation radio selection
		this.querySelectorAll("input[type=radio]").forEach((el) => {
			el.addEventListener("change", () => this.fireChangedEventAndUpdateUI());
		});
	}

	private onFieldValueChange(field: CompensationCreditabilityType, value: number) {
		this._values[field] = value;
		this.selectRadio(field);
		this.fireChangedEventAndUpdateUI();
	}

	private updateUI(): void {
		this.updateInputValues();

		// Update button visibility
		this.updateButtonVisibility();

		// Update info text
		this.updateInfoText();
	}

	private updateInputValues() {
		const compensationDeductibleInput = this.querySelector<HTMLInputElement>("[data-compensation-deductible-input]");
		const nonCreditableDeductibleInput = this.querySelector<HTMLInputElement>("[data-non-creditable-deductible-input]");
		const compensationCreditabilityPercentInput = this.querySelector<HTMLInputElement>("[data-compensation-creditability-percent-input]");
		const creditabilityPercentInput = this.querySelector<HTMLInputElement>("[data-creditability-percent-input]");

		if (nonCreditableDeductibleInput) {
			nonCreditableDeductibleInput.value = this._values.nonCreditableDeductible ? formatDuration(this._values.nonCreditableDeductible) : "";
		}
		if (creditabilityPercentInput) {
			creditabilityPercentInput.value = this._values.creditability != null ? floatToPercent(this._values.creditability).toString() : "";
		}
		if (compensationDeductibleInput) {
			compensationDeductibleInput.value = this._values.compensationDeductible ? formatDuration(this._values.compensationDeductible) : "";
		}
		if (compensationCreditabilityPercentInput) {
			compensationCreditabilityPercentInput.value = this._values.compensationCreditability != null ? floatToPercent(this._values.compensationCreditability).toString() : "";
		}
	}

	private determineMode(): CompensationCreditabilityMode {
		if (this._values.compensationType === null) {
			// creditability or non-creditable deductible is set up, so return Creditability mode
			return CompensationCreditabilityMode.Creditability;
		} else if (this._values.compensationType === CompensationType.WITH_PERCENTAGE_CREDITABILITY ||
			this._values.compensationType === CompensationType.WITH_DURATION_DEDUCTIBLE ||
			this._values.compensationType === CompensationType.WITH_PERCENTAGE_CREDITABILITY_OUTSIDE_DAILY_NORMAL_WORKING_TIME) {
			// compensation creditability or compensation deductible is set up, so return Compensation mode
			return CompensationCreditabilityMode.Compensation;
		} else {
			// all values are set to full creditability and no deductibles, so
			return CompensationCreditabilityMode.FullCreditability;
		}
	}

	private determineRadioValue(): CompensationCreditabilityType {
		if (this._values.creditability !== 1) {
			// return creditability type if it is different from 100%
			return "creditability";
		} else if (this._values.nonCreditableDeductible !== 0) {
			// return nonCreditableDeductible type if it is different from 0 hours
			return "nonCreditableDeductible";
		} else if (this._values.compensationDeductible !== 0) {
			// return compensationDeductible type if it is different from 0 hours
			return "compensationDeductible";
		} else {
			// return compensationCreditability type if it is different from 100% (other values are already checked)
			// Default case, when all values are set to 100% or 0 hours as it is the first value in the list
			return "compensationCreditability";
		}
	}

	private selectRadio(type: CompensationCreditabilityType): void {
		// select the radio button corresponding to the given type
		const radio: HTMLInputElement = this.querySelector(`input[type='radio'][data-radio='${type}']`);
		if (radio) {
			radio.checked = true;
		}

		// clear the values of other inputs that do not match the selected type
		this.querySelectorAll<HTMLInputElement>(`input[type='text']:not([data-creditability-value='${type}']), input[type='number']:not([data-creditability-value='${type}'])`)
			.forEach(el => el.value = "");
	}

	private updateInfoText(): void {
		// hide all info texts
		this.querySelectorAll<HTMLElement>("span[data-creditability-info]").forEach(el => el.style.display = "none");

		// show the info text for the selected creditability mode
		if (this.getMode() !== CompensationCreditabilityMode.FullCreditability) {
			const checkedCompensationConfig: HTMLInputElement = this.querySelector(`input[name='compensationConfig-${this.uuid}']:checked`);
			const checkedCreditabilityConfig: HTMLInputElement = this.querySelector(`input[name='creditabilityConfig-${this.uuid}']:checked`);
			const creditabilityType = this.getMode() === CompensationCreditabilityMode.Compensation ?
				checkedCompensationConfig?.value :
				checkedCreditabilityConfig?.value;
			const creditabilityInfo: HTMLElement = this.querySelector(`span[data-creditability-info="${creditabilityType}"]`);
			if (creditabilityInfo) {
				creditabilityInfo.style.display = "";
			}
		}
	}

	private updateButtonVisibility(): void {
		const buttonCreditability: HTMLElement = this.querySelector("[data-button-creditability]");
		const buttonCompensation: HTMLElement = this.querySelector("[data-button-compensation]");

		if (!buttonCreditability || !buttonCompensation) {
			return;
		}

		const noCompensationTypeSet = this._values.compensationType == null;
		buttonCreditability.style.display = noCompensationTypeSet ? "" : "none";
		buttonCompensation.classList.toggle("btn-border-radius-right", !noCompensationTypeSet);
	}

	private fireChangedEventAndUpdateUI(): void {
		this.updateUI();
		this.dispatchEvent(new CustomEvent(CreditabilityCompensationComponent.EVENT_CHANGED));
	}
}
